import React, { useRef, useCallback, useEffect } from 'react';
import ItemCard from './ItemCard';
import SetCard from './SetCard';
import ItemCardSkeleton from './ItemCardSkeleton';
import './CombinedResultsList.css';

const CombinedResultsList = ({
    results,
    lang = 'fr',
    dense = false,
    skeletonCount = 0,
    onLastResultVisible,
    onItemClick,
    onSetClick,
    onTooltipShow,
    onTooltipHide,
    onSetTooltipShow,
    onSetTooltipHide,
    preferredSlotId = null, showWeaponDamage = false,
    pinnedItem = null,
    pinnedWeaponMaging = null,
    pinnedItemMaging = null,
    onPinnedClick = null,
}) => {
    const observer = useRef();

    const onLastResultVisibleRef = useRef(onLastResultVisible);
    useEffect(() => {
        onLastResultVisibleRef.current = onLastResultVisible;
    }, [onLastResultVisible]);

    const lastResultRef = useCallback(node => {
        if (observer.current) observer.current.disconnect();

        observer.current = new IntersectionObserver(entries => {
            if (entries[0].isIntersecting && onLastResultVisibleRef.current) {
                observer.current.disconnect();
                onLastResultVisibleRef.current();
            }
        });

        if (node) observer.current.observe(node);
    }, []);

    return (
        <div className={`combined-results-list${dense ? ' results-list--dense' : ''}`}>
            {pinnedItem && (
                <ItemCard
                    key="pinned-equipped"
                    item={pinnedItem}
                    lang={lang}
                    dense={dense}
                    isEquipped
                    weaponMaging={pinnedWeaponMaging}
                    itemMaging={pinnedItemMaging}
                    onEquippedClick={onPinnedClick}
                    preferredSlotId={preferredSlotId}
                    showWeaponDamage={showWeaponDamage}
                    onTooltipShow={onTooltipShow}
                    onTooltipHide={onTooltipHide}
                />
            )}
            {results.map((result, index) => {
                const isLastResult = index === results.length - 1;

                // Determine type: primary discriminator is `search_result_type` from backend,
                // then fall back to structure-based detection.
                let resultType = result.search_result_type;
                if (!resultType) {
                    if (result.bonuses || (Array.isArray(result.items) && result.ankama_id === undefined)) {
                        resultType = 'set';
                    } else {
                        resultType = 'item';
                    }
                }

                if (resultType === 'set') {
                    const setKey = result?.id || `set-${index}`;
                    return (
                        <SetCard
                            key={setKey}
                            set={result}
                            lang={lang}
                            dense={dense}
                            onSetClick={onSetClick}
                            onItemClick={onItemClick}
                            onTooltipShow={onTooltipShow}
                            onTooltipHide={onTooltipHide}
                            onSetTooltipShow={onSetTooltipShow}
                            onSetTooltipHide={onSetTooltipHide}
                            preferredSlotId={preferredSlotId}
                            ref={isLastResult ? lastResultRef : null}
                        />
                    );
                }

                const itemId = result?.ankama_id || result?.id;
                const itemKey = itemId ? `${itemId}-${index}` : `item-${index}`;
                return (
                    <ItemCard
                        key={itemKey}
                        item={result}
                        lang={lang}
                        dense={dense}
                        onItemClick={onItemClick}
                        onTooltipShow={onTooltipShow}
                        onTooltipHide={onTooltipHide}
                        preferredSlotId={preferredSlotId}
                        showWeaponDamage={showWeaponDamage}
                        ref={isLastResult ? lastResultRef : null}
                    />
                );
            })}
            {Array.from({ length: skeletonCount }, (_, i) => (
                <ItemCardSkeleton key={`skeleton-${i}`} dense={dense} />
            ))}
        </div>
    );
};

export default CombinedResultsList;
